import axios, { CancelTokenSource } from "axios";
import { uploadFile as uploadFileAPI } from "./uploadFileService";
import Cookies from "js-cookie";
import { Mixpanel } from "../../Mixpanel.js";

export interface UploadFile {
	id: string;
	file: File;
	status: "pending" | "uploading" | "completed" | "failed" | "cancelled";
	progress: number;
	error?: string;
	cancelToken?: CancelTokenSource;
	retryCount: number;
	uploadedItems?: any[];
}

export interface UploadQueueState {
	files: UploadFile[];
	isUploading: boolean;
	maxConcurrentUploads: number;
	maxRetries: number;
}

export interface UploadProgress {
	fileId: string;
	fileName: string;
	progress: number;
	status: UploadFile["status"];
	overallProgress: number;
}

export class SimpleUploadQueue {
	private state: UploadQueueState = {
		files: [],
		isUploading: false,
		maxConcurrentUploads: 5,
		maxRetries: 3,
	};

	private progressCallbacks: Array<(progress: UploadProgress[]) => void> = [];
	private completionCallbacks: Array<
		(results: { completed: UploadFile[]; failed: UploadFile[] }) => void
	> = [];

	constructor(maxConcurrentUploads: number = 5) {
		this.state.maxConcurrentUploads = maxConcurrentUploads;
	}

	// Add files to the queue
	addFiles(files: File[]): void {
		const newFiles: UploadFile[] = files.map((file) => ({
			id: this.generateId(),
			file,
			status: "pending",
			progress: 0,
			retryCount: 0,
		}));

		this.state.files.push(...newFiles);
		this.notifyProgress();
	}

	// Start processing the upload queue
	async startUploads(): Promise<{ uploadedItems: any[] }> {
		if (this.state.isUploading) {
			return { uploadedItems: [] };
		}

		const batchFileIds = this.state.files
			.filter((f) => f.status === "pending" || f.status === "failed")
			.map((f) => f.id);

		// If nothing new to upload, bail out early
		if (batchFileIds.length === 0) {
			return { uploadedItems: [] };
		}

		this.state.isUploading = true;
		await this.processQueue();

		// After processing, collect uploadedItems ONLY for files in this batch
		const uploadedItems = this.state.files
			.filter(
				(f) =>
					batchFileIds.includes(f.id) &&
					f.status === "completed" &&
					Array.isArray(f.uploadedItems),
			)
			.flatMap((f) => f.uploadedItems as any[]);

		return { uploadedItems };
	}

	// Process the upload queue with concurrency limits
	private async processQueue(): Promise<void> {
		const pendingFiles = this.state.files.filter((f) => f.status === "pending");

		if (pendingFiles.length === 0) {
			this.completeAllUploads();
			return;
		}

		const batch = pendingFiles.slice(0, this.state.maxConcurrentUploads);
		const uploadPromises = batch.map((file) => this.uploadSingleFile(file));

		await Promise.allSettled(uploadPromises);

		// Continue processing if there are more files
		const remainingPending = this.state.files.filter(
			(f) => f.status === "pending",
		);
		if (remainingPending.length > 0) {
			await this.processQueue();
		} else {
			this.completeAllUploads();
		}
	}

	// Upload a single file using the existing API
	private async uploadSingleFile(uploadFile: UploadFile): Promise<void> {
		try {
			uploadFile.status = "uploading";
			uploadFile.cancelToken = axios.CancelToken.source();
			this.notifyProgress();

			const formData = new FormData();
			formData.append("file1", uploadFile.file);

			// Track upload progress
			uploadFile.progress = 10; // Start with some progress
			this.notifyProgress();

			const response = await uploadFileAPI(formData);

			if (response.data?.status === "green") {
				uploadFile.status = "completed";
				uploadFile.progress = 100;

				const newItems = Array.isArray(response.data?.data)
					? response.data.data
					: [];
				uploadFile.uploadedItems = newItems;

				const newCodes = newItems?.map((item: any) => item?.item_code);
				const fileNames = newItems?.map((item: any) => item?.item_name);
				try {
					if (newCodes.length > 0) {
						const raw = JSON.parse(
							localStorage.getItem("uploadedFiles") || "[]",
						);
						const existing: string[] = Array.isArray(raw) ? raw : [];
						const merged = Array.from(new Set([...existing, ...newCodes]));

						localStorage.setItem("uploadedFiles", JSON.stringify(merged));
						(window as any).dataLayer?.push({
							event: "Files Uploaded",
							page_name: "File Upload",
							page_url: window.location.href,
							user_id: Cookies.get("user_id"),
							file_names: fileNames?.map((each: any) => `${each},`),
							alert_message: "File Upload Success",
						});
						Mixpanel.track("Files Uploaded", {
							page_name: "File Upload",
							page_url: window.location.href,
							user_id: Cookies.get("user_id"),
							file_names: fileNames?.map((each: any) => `${each},`),
							alert_message: "File Upload Success",
							email_id: Cookies.get("user_id"),
						});
					}
				} catch (e) {
					console.error("Failed to update uploadedFiles in localStorage", e);
					// As a fallback, at least store the latest batch
					try {
						localStorage.setItem("uploadedFiles", JSON.stringify(newCodes));
					} catch {
						// ignore
					}
				}
			} else {
				throw new Error(response.data?.message || "Upload failed");
			}

			this.notifyProgress();
		} catch (error) {
			await this.handleUploadError(uploadFile, error);
		}
	}

	// Handle upload errors with retry logic
	private async handleUploadError(
		uploadFile: UploadFile,
		error: any,
	): Promise<void> {
		if (axios.isCancel(error)) {
			uploadFile.status = "cancelled";
			return;
		}

		uploadFile.retryCount++;

		if (uploadFile.retryCount <= this.state.maxRetries) {
			// Retry the upload after a delay
			uploadFile.status = "pending";
			uploadFile.error = undefined;

			// Exponential backoff: 2^retryCount seconds
			const delay = Math.pow(2, uploadFile.retryCount) * 1000;
			await new Promise((resolve) => setTimeout(resolve, delay));

			// Retry the upload
			await this.uploadSingleFile(uploadFile);
		} else {
			// Max retries exceeded
			uploadFile.status = "failed";
			uploadFile.error = error.message || "Upload failed after maximum retries";
			this.notifyProgress();
			(window as any).dataLayer?.push({
				event: "Files Uploaded",
				page_name: "File Upload",
				page_url: window.location.href,
				user_id: Cookies.get("user_id"),
				alert_message: "File Upload Failed",
			});
			Mixpanel.track("Files Uploaded", {
				page_name: "File Upload",
				page_url: window.location.href,
				user_id: Cookies.get("user_id"),
				alert_message: "File Upload Failed",
				email_id: Cookies.get("user_id"),
			});
		}
	}

	// Generate unique ID for files
	private generateId(): string {
		return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	// Cancel a specific file upload
	cancelFile(fileId: string): void {
		const file = this.state.files.find((f) => f.id === fileId);
		if (file && file.status === "uploading") {
			file.cancelToken?.cancel();
			file.status = "cancelled";
			this.notifyProgress();
		}
	}

	// Cancel all uploads
	cancelAll(): void {
		this.state.files.forEach((file) => {
			if (file.status === "uploading" || file.status === "pending") {
				file.cancelToken?.cancel();
				file.status = "cancelled";
			}
		});
		this.state.isUploading = false;
		this.notifyProgress();
	}

	// Retry a failed upload
	async retryFile(fileId: string): Promise<void> {
		const file = this.state.files.find((f) => f.id === fileId);
		if (file && file.status === "failed") {
			file.status = "pending";
			file.error = undefined;
			file.retryCount = 0; // Reset retry count
			this.notifyProgress();

			if (!this.state.isUploading) {
				await this.startUploads();
			}
		}
	}

	// Clear completed files from the queue
	clearCompleted(): void {
		this.state.files = this.state.files.filter((f) => f.status !== "completed");
		this.notifyProgress();
	}

	// Get current upload progress
	getProgress(): UploadProgress[] {
		return this.state.files.map((file) => ({
			fileId: file.id,
			fileName: file.file.name,
			progress: file.progress,
			status: file.status,
			overallProgress: this.calculateOverallProgress(),
		}));
	}

	// Calculate overall progress across all files
	private calculateOverallProgress(): number {
		if (this.state.files.length === 0) return 0;

		const totalProgress = this.state.files.reduce(
			(sum, file) => sum + file.progress,
			0,
		);
		return Math.round(totalProgress / this.state.files.length);
	}

	// Subscribe to progress updates
	onProgress(callback: (progress: UploadProgress[]) => void): () => void {
		this.progressCallbacks.push(callback);

		// Return unsubscribe function
		return () => {
			this.progressCallbacks = this.progressCallbacks.filter(
				(cb) => cb !== callback,
			);
		};
	}

	// Subscribe to completion events
	onComplete(
		callback: (results: {
			completed: UploadFile[];
			failed: UploadFile[];
		}) => void,
	): () => void {
		this.completionCallbacks.push(callback);

		// Return unsubscribe function
		return () => {
			this.completionCallbacks = this.completionCallbacks.filter(
				(cb) => cb !== callback,
			);
		};
	}

	// Notify progress subscribers
	private notifyProgress(): void {
		const progress = this.getProgress();
		this.progressCallbacks.forEach((callback) => callback(progress));
	}

	// Complete all uploads and notify subscribers
	private completeAllUploads(): void {
		this.state.isUploading = false;

		const completed = this.state.files.filter((f) => f.status === "completed");
		const failed = this.state.files.filter((f) => f.status === "failed");

		this.completionCallbacks.forEach((callback) =>
			callback({ completed, failed }),
		);
		this.notifyProgress();
	}

	// Get current queue state
	getQueueState(): UploadQueueState {
		return { ...this.state };
	}

	// Clear the entire queue
	clearQueue(): void {
		this.cancelAll();
		this.state.files = [];
		this.notifyProgress();
	}
}

// Global upload queue instance using simple uploads (compatible with existing backend)
export const simpleUploadQueue = new SimpleUploadQueue(5);
